namespace e5 {
    export interface IListData {
        visible: boolean;
        height?: number;
    }

    export interface IListVisual {
        element: HTMLDivElement;
    }

    export interface IListProvider<Data extends IListData, Visual extends IListVisual> {
        prepare(visual: Visual): void;
        update(data: Data, visual: Visual): void;
        dataList: Data[];
    }

    interface DataLookup<Data extends IListData, Visual extends IListVisual> {
        index: number;
        data?: Data;
        visual?: Visual;
        element: HTMLDivElement;
    }

    export interface ScrollOptions {
        tween?: number;
        align?: "top" | "bottom" | "auto";
        marginTop?: number;
        marginBottom?: number;
        ifNeeded?: boolean;
    }

    export class List<Data extends IListData = IListData, Visual extends IListVisual = IListVisual> {
        private _element: HTMLElement = null;
        private _provider: IListProvider<Data, Visual> = null;
        private _content: HTMLDivElement;
        private _dataLookup: DataLookup<Data, Visual>[] = [];
        private _update: () => void;
        private _visible: boolean = true;

        constructor(private height: number, element: HTMLElement = null, private _offset: number = 0) {
            this._content = document.createElement("div");
            this._content.classList.add("e5-list-content");
            this._update = () => this.update();
            window.addEventListener("resize", this._update);

            this.element = element;
        }

        public get visible(): boolean {
            return this._visible;
        }

        public set visible(visible: boolean) {
            if (this._visible === visible)
                return;
            this._visible = visible;
            this._content.classList.toggle("hidden", !visible);
            if (visible)
                this.update();
        }

        public scrollTo(item: Data, options: ScrollOptions = {}): void {
            if (!this._visible || !item || !item.visible || !this._element || !this._provider)
                return;
            var items = this.getDataList();
            var index = items.indexOf(item);
            if (index === -1) {
                console.warn("You cannot scroll to an element which is not part of the provider items.");
                return;
            }

            var align = options.align || "auto";
            var tween = options.tween === undefined ? 0 : options.tween;
            var ifNeeded = options.ifNeeded === undefined ? true : options.ifNeeded;
            var marginTop = options.marginTop === undefined ? 0 : options.marginTop;
            var marginBottom = options.marginBottom === undefined ? 0 : options.marginBottom;

            var offsetTop = index * this.height + marginTop + this._content.offsetTop - this._element.scrollTop;
            var offsetBottom = this._element.clientHeight - (marginBottom + (index + 1) * this.height + this._content.offsetTop - this._element.scrollTop);

            if (ifNeeded && offsetTop > 0 && offsetBottom > 0)
                return;

            if (align === "auto") {
                if (offsetTop < 0)
                    align = "top";
                else
                    align = "bottom";
            }

            var scrollTop: number;
            if (align === "top") {
                scrollTop = index * this.height + this._content.offsetTop - marginTop;
            }
            else if (align === "bottom") {
                scrollTop = index * this.height + this._content.offsetTop;
                scrollTop -= this._element.clientHeight - this.height - marginBottom;
            }

            this.scrollTop(scrollTop, tween);
        }

        public scrollTop(scrollTop: number, tween: number = 0): void {
            if (!this._visible || !this._element)
                return;
            scrollTop = Calc.clamp(scrollTop, 0, this._element.scrollHeight - this._element.clientHeight);

            if (tween <= 0) {
                this._element.scrollTop = scrollTop;
                this.update();
            } else {
                gs.TweenMax.to(this._element, tween, { scrollTop: scrollTop, onUpdate: () => this.update() });
            }
        }

        public refresh(): void {
            for (var lookupItem of this._dataLookup) {
                lookupItem.index = -1;
                if (lookupItem.element)
                    lookupItem.element.remove();
            }
            this.update();
        }

        public updateData(data: Data): void {
            if (!data)
                return;
            for (var dataLookup of this._dataLookup) {
                if (dataLookup.data === data) {
                    this._provider.update(dataLookup.data, dataLookup.visual);
                }
            }
        }

        private getDataList(): Data[] {
            var items: Data[] = [];
            for (var item of this._provider.dataList) {
                if (item.visible)
                    items.push(item);
            }
            return items;
        }

        public update(): void {
            if (!this._visible || !this._element || !this._provider) {
                this._content.style.height = "0";
                return;
            }

            //Get all visible items and
            var dataList = this.getDataList();
            var height = dataList.length * this.height;
            this._content.style.height = (height + this._offset) + "px";

            //Create lookup item buffer.
            var countLookupItems = Math.ceil(this._element.offsetHeight / this.height) + 2;
            if (this._dataLookup.length < countLookupItems) {
                var diff = countLookupItems - this._dataLookup.length;
                for (var dataIndex = 0; dataIndex < diff; ++dataIndex) {
                    var element = document.createElement("div");
                    element.classList.add("e5-list-item");
                    // element.style.height = this._provider.height + "px";
                    var visual: Visual = <any>{ element: element };
                    var lookup: DataLookup<Data, Visual> = { visual: visual, index: -1, element: element, data: null };
                    this._provider.prepare(visual);
                    this._dataLookup.push(lookup);
                }
            }

            var firstIndex = Math.floor(e5.Calc.clampMin(this._element.scrollTop - this._content.offsetTop, 0) / this.height);
            var maxIndex = Calc.clamp(firstIndex + (countLookupItems - 1), 0, dataList.length - 1);

            //Remove and reset all lookup items which are not needed.
            for (var dataLookup of this._dataLookup) {
                if (dataLookup.index !== -1 && (dataLookup.index < firstIndex || dataLookup.index > maxIndex)) {
                    dataLookup.element.remove();
                    dataLookup.index = -1;
                }
            }

            //Update and move the item to correct position.
            for (var dataIndex = firstIndex; dataIndex <= maxIndex; ++dataIndex) {
                var dataLookup = this.getDataLookup(dataIndex);
                if (!dataList[dataIndex])
                    continue;
                if (dataLookup.index === -1) {
                    this._provider.update(dataList[dataIndex], dataLookup.visual);
                    this._content.appendChild(dataLookup.element);
                    dataLookup.element.style.top = (dataIndex * this.height) + "px";
                    dataLookup.index = dataIndex;
                }
            }
        }

        private getDataLookup(index: number): DataLookup<Data, Visual> {
            for (var item of this._dataLookup)
                if (item.index === index)
                    return item;
            for (var item of this._dataLookup)
                if (item.index === -1)
                    return item;
            return null;
        }

        private setElement(): void {
            this._element.appendChild(this._content);
            this._element.addEventListener("scroll", this._update, Caps.passiveEventLister ? { passive: true } : false);
            this.refresh();
        }

        private unsetElement(): void {
            this._content.remove();
            this._element.removeEventListener("scroll", this._update, false);
        }

        public get element(): HTMLElement {
            return this._element;
        }

        public set element(element: HTMLElement) {
            if (this._element === element)
                return;
            if (this._element)
                this.unsetElement();
            this._element = element;
            if (this._element)
                this.setElement();
        }

        public get provider(): IListProvider<Data, Visual> {
            return this._provider;
        }

        public set provider(provider: IListProvider<Data, Visual>) {
            if (this._provider === provider)
                return;
            this._provider = provider;
            this.refresh();
        }

        public get contentElement(): HTMLDivElement {
            return this._content;
        }
    }
}